import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Attraction,
  AttractionCreateInput,
  AttractionUpdateInput,
  AvatarFit,
  FaceFit,
} from '@underline/shared';
import { api } from '@/lib/api';

interface ListParams {
  q?: string;
  brand_id?: number;
  limit?: number;
}

export function useAttractions(params: ListParams = {}) {
  return useQuery({
    queryKey: ['attractions', params],
    queryFn: () =>
      api
        .get('attractions', {
          searchParams: Object.fromEntries(
            Object.entries(params).filter(([, v]) => v != null && v !== ''),
          ) as Record<string, string | number>,
        })
        .json<Attraction[]>(),
    staleTime: 30 * 1000,
  });
}

export function useCreateAttraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AttractionCreateInput) =>
      api.post('attractions', { json: input }).json<Attraction>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}

export function useUpdateAttraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: AttractionUpdateInput }) =>
      api.patch(`attractions/${id}`, { json: input }).json<Attraction>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}

export function useDeleteAttraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`attractions/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}

/** Foto de perfil quadrada — upload que preserva original, retorna dimensões (admin+gerente). */
export function useUploadAttractionPhotoOriginal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api
        .post(`attractions/${id}/photo`, { body: fd })
        .json<{ photo_url: string; img_w: number; img_h: number }>();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}

/** Aplica zoom/offset na foto de perfil do DJ a partir da original salva. */
export function useSaveAttractionPhotoFit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fit }: { id: number; fit: AvatarFit }) =>
      api.patch(`attractions/${id}/photo-fit`, { json: fit }).json<{ photo_url: string }>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}

/** Retrato vertical do card — só converte e salva o arquivo (admin only). */
export function useUploadAttractionPortrait() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api
        .post(`attractions/${id}/portrait`, { body: fd })
        .json<{ portrait_url: string; img_w: number; img_h: number }>();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}

/** Salva só a posição/zoom/rotação do rosto na moldura (admin only). */
export function useSaveAttractionFaceFit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, faceFit }: { id: number; faceFit: FaceFit }) =>
      api
        .patch(`attractions/${id}/face-fit`, { json: { face_fit: faceFit } })
        .json<{ face_fit: FaceFit }>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attractions'] });
    },
  });
}
